import React, { useEffect, useState } from "react";
import AppLayout from "../componets/AppLayout/AppLayout";
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Stack,
  Autocomplete,
  Chip,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { MembershipAPI } from "../api/membership";
import { BulkAPI } from "../api/bulk";
import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";

const bulkTabs = [
  { label: "Meet Up" },
  { label: "Referral" },
  { label: "Visitor" },
  { label: "Closed Business" },
];

const sortByFullName = (arr) =>
  [...arr].sort((a, b) => {
    const aName = `${a.first_name || ""} ${a.last_name || ""}`
      .trim()
      .toLowerCase();
    const bName = `${b.first_name || ""} ${b.last_name || ""}`
      .trim()
      .toLowerCase();
    return aName.localeCompare(bName);
  });

const BulkPage = () => {
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [activeTab, setActiveTab] = useState(0);
  const [members, setMembers] = useState([]);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { user } = useAuth();
  const { openSnack } = useAppContext();

  const currentMemberId = user?.id || null;

  const [meetupDate, setMeetupDate] = useState(null);
  const [meetupMemberIds, setMeetupMemberIds] = useState([]);

  const [referralDate, setReferralDate] = useState(null);
  const [referralSenderId, setReferralSenderId] = useState("");
  const [referralReceiverId, setReferralReceiverId] = useState("");
  const [referralName, setReferralName] = useState("");

  const [visitorFirstName, setVisitorFirstName] = useState("");
  const [visitorLastName, setVisitorLastName] = useState("");
  const [visitorDate, setVisitorDate] = useState(null);
  const [visitorChair, setVisitorChair] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");

  const [cbClientName, setCbClientName] = useState("");
  const [cbAmount, setCbAmount] = useState("");
  const [cbDate, setCbDate] = useState(null);
  const [cbReferringId, setCbReferringId] = useState("");
  const [cbReceivingId, setCbReceivingId] = useState("");

  const [cbRecurrent, setCbRecurrent] = useState(false);
  const [cbPeriod, setCbPeriod] = useState("");
  const [cbFirstDayPayment, setCbFirstDayPayment] = useState(null);
  const [cbLastDayPayment, setCbLastDayPayment] = useState(null);
  const [cbAmountPerPeriod, setCbAmountPerPeriod] = useState("");

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setIsMembersLoading(true);
        const res = await MembershipAPI.getMemberDirectory("active");
        setMembers(res?.items || []);
      } catch (e) {
        console.error("Error fetching members for bulk page", e);
      } finally {
        setIsMembersLoading(false);
      }
    };

    fetchMembers();
  }, []);

  const handleReferralSenderChange = (e) => {
    const value = e.target.value || "";
    setReferralSenderId(value);

    if (
      currentMemberId &&
      Number(value) !== currentMemberId &&
      Number(referralReceiverId) !== currentMemberId
    ) {
      setReferralReceiverId(String(currentMemberId));
    }
  };

  const handleReferralReceiverChange = (e) => {
    const value = e.target.value || "";
    setReferralReceiverId(value);

    if (
      currentMemberId &&
      Number(value) !== currentMemberId &&
      Number(referralSenderId) !== currentMemberId
    ) {
      setReferralSenderId(String(currentMemberId));
    }
  };

  const handleCbReferringChange = (e) => {
    const value = e.target.value || "";
    setCbReferringId(value);
    if (
      currentMemberId &&
      Number(value) !== currentMemberId &&
      Number(cbReceivingId) !== currentMemberId
    ) {
      setCbReceivingId(String(currentMemberId));
    }
  };

  const handleCbReceivingChange = (e) => {
    const value = e.target.value || "";
    setCbReceivingId(value);
    if (
      currentMemberId &&
      Number(value) !== currentMemberId &&
      Number(cbReferringId) !== currentMemberId
    ) {
      setCbReferringId(String(currentMemberId));
    }
  };

  const resetAll = () => {
    setMeetupDate(null);
    setMeetupMemberIds([]);
    setReferralDate(null);
    setReferralSenderId("");
    setReferralReceiverId("");
    setReferralName("");
    setVisitorFirstName("");
    setVisitorLastName("");
    setVisitorDate(null);
    setVisitorChair("");
    setVisitorPhone("");
    setVisitorEmail("");
    setCbClientName("");
    setCbAmount("");
    setCbDate(null);
    setCbReferringId("");
    setCbReceivingId("");
    setCbRecurrent(false);
    setCbPeriod("");
    setCbFirstDayPayment(null);
    setCbLastDayPayment(null);
    setCbAmountPerPeriod("");
  };

  const handleSend = async () => {
    if (!currentMemberId) {
      openSnack("Current member is not defined", "error");
      return;
    }

    const payload = {};

    if (meetupDate || meetupMemberIds.length > 0) {
      if (!meetupDate || meetupMemberIds.length < 2) {
        openSnack(
          "For Meet Up, please select a date and at least two members.",
          "error",
        );
        return;
      }

      payload.meetups = [
        {
          meetup_date: dayjs(meetupDate).format("YYYY-MM-DD"),
          added_active_members_ids: meetupMemberIds.map((id) => Number(id)),
        },
      ];
    }

    if (referralDate && referralSenderId && referralReceiverId) {
      if (Number(referralSenderId) === Number(referralReceiverId)) {
        openSnack(
          "Referral Sender and Referral Receiver can not be the same person.",
          "error",
        );
        return;
      }

      if (
        Number(referralSenderId) !== currentMemberId &&
        Number(referralReceiverId) !== currentMemberId
      ) {
        openSnack(
          "In Referral, one of 'Referral Sender' or 'Referral Receiver' must be you.",
          "error",
        );
        return;
      }

      payload.referrals = [
        {
          referral_name: referralName || "",
          referral_date: dayjs(referralDate).format("YYYY-MM-DD"),
          referral_sender_id: Number(referralSenderId),
          referral_receiver_id: Number(referralReceiverId),
        },
      ];
    }

    if (visitorFirstName && visitorLastName && visitorDate) {
      payload.visitors = [
        {
          visitor_first_name: visitorFirstName,
          visitor_last_name: visitorLastName,
          date_visited: dayjs(visitorDate).format("YYYY-MM-DD"),
          chair: visitorChair,
          phone_number: visitorPhone,
          email_address: visitorEmail,
          member_who_brought_visitor_id: currentMemberId,
        },
      ];
    }

    if (cbClientName && cbAmount && cbDate && cbReferringId && cbReceivingId) {
      if (cbRecurrent) {
        if (!cbPeriod || !cbFirstDayPayment || !cbAmountPerPeriod) {
          openSnack(
            "For recurrent payment, please fill Payment Period, First Day of Payment and Dollar Amount per Period.",
            "error",
          );
          return;
        }
      }

      if (
        Number(cbReferringId) !== currentMemberId &&
        Number(cbReceivingId) !== currentMemberId
      ) {
        openSnack(
          "In Closed Business, one of 'Referring' or 'Receiving' must be you.",
          "error",
        );
        return;
      }

      payload.independent_closed_businesses = [
        {
          closed_business_name: cbClientName,
          closed_business_date: dayjs(cbDate).format("YYYY-MM-DD"),
          dollar_amount_of_realized_revenue: Number(cbAmount),
          closed_business_sender_id: Number(cbReferringId),
          closed_business_receiver_id: Number(cbReceivingId),
          recurrent: cbRecurrent || false,
          period: cbRecurrent ? cbPeriod : null,
          first_day_of_payment:
            cbRecurrent && cbFirstDayPayment
              ? dayjs(cbFirstDayPayment).format("YYYY-MM-DD")
              : null,
          last_day_of_payment:
            cbRecurrent && cbLastDayPayment
              ? dayjs(cbLastDayPayment).format("YYYY-MM-DD")
              : null,
          dollar_amount_per_period:
            cbRecurrent && cbAmountPerPeriod ? Number(cbAmountPerPeriod) : null,
        },
      ];
    }

    if (Object.keys(payload).length === 0) {
      openSnack("Nothing to send. Please fill at least one section.", "error");
      return;
    }

    try {
      setIsSending(true);
      await BulkAPI.sendBulkData(payload);
      openSnack("Bulk data sent successfully.", "success");
      resetAll();
    } catch (e) {
      console.error("Error sending bulk data", e);
      openSnack("Error sending bulk data.", "error");
    } finally {
      setIsSending(false);
    }
  };

  const sortedMembers = sortByFullName(members);

  return (
    <AppLayout>
      <Box
        sx={{
          width: "100%",
          padding: isMobileTable ? "8px" : "24px 24px 12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            padding: isMobileTable ? "8px" : "24px 24px 12px",
            borderRadius: "8px",
            border: "1px solid #E2E8F0",
            height: "64px",
          }}
        >
          <h5 className="grid-header">Enter member data</h5>

          <Button
            variant="contained"
            onClick={handleSend}
            disabled={isSending || isMembersLoading}
            sx={{
              width: "auto",
              px: 4,
              borderRadius: "999px",
              textTransform: "none",
            }}
          >
            {isSending ? "Submiting..." : "Submit"}
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            marginTop: "12px",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "#fff",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              padding: "8px",
            }}
            sm={{
              padding: "24px",
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                overflowX: "auto",

                "&::-webkit-scrollbar": {
                  height: 1,
                },
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => {
                  setActiveTab(newValue);
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="basic tabs example"
                sx={{
                  minWidth: "max-content",

                  "& .MuiTabs-flexContainer": {
                    flexWrap: "nowrap",
                  },
                }}
              >
                {bulkTabs.map((tab) => (
                  <Tab
                    key={tab.label}
                    label={tab.label}
                    sx={{
                      minHeight: "40px",
                      fontSize: "16px",
                      lineHeight: "24px",
                      textTransform: "capitalize",
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            {/* MEET UP */}
            {activeTab === 0 && (
              <Stack
                className="tab-layout"
                sx={{ paddingTop: 3, maxWidth: 600 }}
                gap={2}
              >
                <DatePicker
                  label="Date"
                  value={meetupDate}
                  onChange={setMeetupDate}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      className: "login-textField",
                      size: "small",
                    },
                  }}
                />

                <Autocomplete
                  multiple
                  limitTags={3}
                  value={sortedMembers.filter((m) =>
                    meetupMemberIds.includes(m.id),
                  )}
                  onChange={(_e, newValue) =>
                    setMeetupMemberIds(newValue.map((m) => m.id))
                  }
                  options={sortedMembers}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor: selected && "#EAF2FF",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      {`${option.first_name} ${option.last_name}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Member(s) met with"
                      className="login-textField"
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    const numTags = value.length;
                    const limitTags = 3;

                    return (
                      <>
                        {value.slice(0, limitTags).map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={index}
                            label={`${option.first_name} ${option.last_name}`}
                            sx={{
                              backgroundColor: "#EAF2FF",
                              color: "#0F172A",
                              fontSize: "12px",
                              lineHeight: "18px",
                              height: "24px",
                            }}
                          />
                        ))}

                        {numTags > limitTags && ` +${numTags - limitTags}`}
                      </>
                    );
                  }}
                  className="login-textField"
                  size="small"
                />
              </Stack>
            )}

            {/* REFERRAL */}
            {activeTab === 1 && (
              <Stack
                className="tab-layout"
                sx={{ paddingTop: 3, maxWidth: 600 }}
                gap={2}
              >
                <DatePicker
                  label="Referral Date"
                  value={referralDate}
                  onChange={setReferralDate}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      className: "login-textField",
                      size: "small",
                    },
                  }}
                />

                <Autocomplete
                  value={
                    sortedMembers.find(
                      (m) => m.id === Number(referralSenderId),
                    ) || null
                  }
                  onChange={(_e, newValue) =>
                    handleReferralSenderChange({
                      target: { value: newValue ? newValue.id : "" },
                    })
                  }
                  options={sortedMembers}
                  getOptionLabel={(option) =>
                    `${option?.first_name} ${option?.last_name}`
                  }
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor: selected && "#EAF2FF",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      {`${option?.first_name} ${option?.last_name}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Referral Sender"
                      className="login-textField"
                      size="small"
                    />
                  )}
                  className="login-textField"
                  size="small"
                />

                <Autocomplete
                  value={
                    sortedMembers.find(
                      (m) => m.id === Number(referralReceiverId),
                    ) || null
                  }
                  onChange={(_e, newValue) =>
                    handleReferralReceiverChange({
                      target: { value: newValue ? newValue.id : "" },
                    })
                  }
                  options={sortedMembers}
                  getOptionLabel={(option) =>
                    `${option?.first_name} ${option?.last_name}`
                  }
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor: selected && "#EAF2FF",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      {`${option?.first_name} ${option?.last_name}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Referral Receiver"
                      className="login-textField"
                      size="small"
                    />
                  )}
                  className="login-textField"
                  size="small"
                />

                <TextField
                  label="Referral Name"
                  fullWidth
                  className="login-textField"
                  size="small"
                  value={referralName}
                  onChange={(e) => setReferralName(e.target.value)}
                />
              </Stack>
            )}

            {/* VISITOR */}
            {activeTab === 2 && (
              <Stack
                className="tab-layout"
                sx={{ paddingTop: 3, maxWidth: 600 }}
                gap={2}
              >
                <TextField
                  label="Visitor First Name"
                  fullWidth
                  className="login-textField"
                  size="small"
                  value={visitorFirstName}
                  onChange={(e) => setVisitorFirstName(e.target.value)}
                />
                <TextField
                  label="Visitor Last Name"
                  fullWidth
                  className="login-textField"
                  size="small"
                  value={visitorLastName}
                  onChange={(e) => setVisitorLastName(e.target.value)}
                />
                <DatePicker
                  label="Date Visited"
                  value={visitorDate}
                  onChange={setVisitorDate}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      className: "login-textField",
                      size: "small",
                    },
                  }}
                />
                <TextField
                  label="Chair"
                  fullWidth
                  className="login-textField"
                  size="small"
                  value={visitorChair}
                  onChange={(e) => setVisitorChair(e.target.value)}
                />
                <TextField
                  label="Visitor Phone Number"
                  fullWidth
                  className="login-textField"
                  size="small"
                  value={visitorPhone}
                  onChange={(e) => setVisitorPhone(e.target.value)}
                />
                <TextField
                  label="Email Address"
                  fullWidth
                  className="login-textField"
                  size="small"
                  value={visitorEmail}
                  onChange={(e) => setVisitorEmail(e.target.value)}
                />
              </Stack>
            )}

            {/* CLOSED BUSINESS */}
            {activeTab === 3 && (
              <Stack
                className="tab-layout"
                sx={{ paddingTop: 3, maxWidth: 600 }}
                gap={2}
              >
                <Autocomplete
                  value={
                    sortedMembers.find((m) => m.id === Number(cbReferringId)) ||
                    null
                  }
                  onChange={(_e, newValue) =>
                    handleCbReferringChange({
                      target: { value: newValue ? newValue.id : "" },
                    })
                  }
                  options={sortedMembers}
                  getOptionLabel={(option) =>
                    `${option?.first_name} ${option?.last_name}`
                  }
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor: selected && "#EAF2FF",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      {`${option?.first_name} ${option?.last_name}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Referring Member"
                      className="login-textField"
                      size="small"
                    />
                  )}
                  className="login-textField"
                  size="small"
                />

                <Autocomplete
                  value={
                    sortedMembers.find((m) => m.id === Number(cbReceivingId)) ||
                    null
                  }
                  onChange={(_e, newValue) =>
                    handleCbReceivingChange({
                      target: { value: newValue ? newValue.id : "" },
                    })
                  }
                  options={sortedMembers}
                  getOptionLabel={(option) =>
                    `${option?.first_name} ${option?.last_name}`
                  }
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor: selected && "#EAF2FF",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      {`${option?.first_name} ${option?.last_name}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Receiving Member"
                      className="login-textField"
                      size="small"
                    />
                  )}
                  className="login-textField"
                  size="small"
                />

                <TextField
                  label="Client"
                  fullWidth
                  className="login-textField"
                  size="small"
                  value={cbClientName}
                  onChange={(e) => setCbClientName(e.target.value)}
                />

                <TextField
                  label="Dollar Amount"
                  fullWidth
                  className="login-textField"
                  size="small"
                  type="number"
                  value={cbAmount}
                  onChange={(e) => setCbAmount(e.target.value)}
                />

                <DatePicker
                  label="Date Closed"
                  value={cbDate}
                  onChange={setCbDate}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      className: "login-textField",
                      size: "small",
                    },
                  }}
                />

                <Box sx={{ pt: 1 }}>
                  <h3
                    style={{
                      margin: "8px 0 16px 0",
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    Recurrent Payment Settings
                  </h3>

                  <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                    <TextField
                      select
                      fullWidth
                      label="Recurrent Payment"
                      className="login-textField"
                      size="small"
                      value={cbRecurrent ? "true" : "false"}
                      onChange={(e) =>
                        setCbRecurrent(e.target.value === "true")
                      }
                    >
                      <MenuItem value="false">No</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                    </TextField>

                    {cbRecurrent && (
                      <TextField
                        select
                        fullWidth
                        label="Payment Period*"
                        className="login-textField"
                        size="small"
                        value={cbPeriod}
                        onChange={(e) => setCbPeriod(e.target.value)}
                      >
                        <MenuItem value="">Select Period</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="quarter">Quarter</MenuItem>
                        <MenuItem value="year">Year</MenuItem>
                      </TextField>
                    )}
                  </Stack>

                  {cbRecurrent && (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      gap={2}
                      sx={{ mt: 2 }}
                    >
                      <DatePicker
                        label="First Day of Payment*"
                        value={cbFirstDayPayment}
                        onChange={setCbFirstDayPayment}
                        format="MM/DD/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            className: "login-textField",
                            size: "small",
                          },
                        }}
                      />

                      <DatePicker
                        label="Last Day of Payment (Optional)"
                        value={cbLastDayPayment}
                        onChange={setCbLastDayPayment}
                        format="MM/DD/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            className: "login-textField",
                            size: "small",
                          },
                        }}
                        minDate={cbFirstDayPayment}
                      />
                    </Stack>
                  )}

                  {cbRecurrent && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Dollar Amount per Period*"
                        fullWidth
                        className="login-textField"
                        size="small"
                        type="number"
                        value={cbAmountPerPeriod}
                        onChange={(e) => setCbAmountPerPeriod(e.target.value)}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </AppLayout>
  );
};

export default BulkPage;
